import React, { useState, useMemo } from 'react';
import {
  Modal,
  Form,
  Table,
  notification,
  Typography,
  Input,
  Checkbox,
} from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { QUERY_FEE_LEDGER_RECORDS_TO_REFUND } from '../../queries/FeeLedgerQueries';
import { CURRENCY_FORMAT } from '../../constants/numberformats';
import numeral from 'numeral';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import routes from '../../constants/routes';
import {
  MUTATION_REFUND_ASSIGNMENT,
  QUERY_ASSIGNMENTS,
} from '../../queries/AssignmentsQueries';
import { QUERY_ASSIGNMENT_FEE_LEDGER } from '../../queries/FeeLedgerQueries';
import { PaymentProcessorIds } from '../../constants/PaymentProcessors';

const STRIPE_INVOICE_URL = `https://dashboard.stripe.com/${
  process.env.REACT_APP_ENV === 'production' ? '' : 'test/'
}invoices/`;

interface RefundFeeLedgerModalProps {
  isVisible: boolean;
  onClose: () => void;
  assignmentId: string;
  onSuccess?: () => void;
}

const RefundFeeLedgerModal: React.FC<RefundFeeLedgerModalProps> = ({
  isVisible,
  onClose,
  assignmentId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [isRefunding, setIsRefunding] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const { data: refundableRecords } = useQuery(
    QUERY_FEE_LEDGER_RECORDS_TO_REFUND,
    {
      variables: { assignment_id: assignmentId },
      skip: !assignmentId || !isVisible,
    },
  );

  const [refundAssignment] = useMutation<{ refundAssignment: boolean }>(
    MUTATION_REFUND_ASSIGNMENT,
    {
      refetchQueries: [QUERY_ASSIGNMENTS, QUERY_ASSIGNMENT_FEE_LEDGER],
    },
  );

  const isStripePaymentProcessor = useMemo(() => {
    if (!refundableRecords?.getFeeLedgerRecordsToRefund?.length) return false;

    const firstRecord = refundableRecords.getFeeLedgerRecordsToRefund[0];
    const userMembership =
      firstRecord.assignment?.user?.user_membership?.[
        firstRecord.assignment?.user?.user_membership.length - 1
      ];

    return userMembership?.payment_processor_id === PaymentProcessorIds.Stripe;
  }, [refundableRecords]);

  const groupedData = useMemo(() => {
    if (
      !refundableRecords?.getFeeLedgerRecordsToRefund ||
      !isStripePaymentProcessor
    ) {
      return refundableRecords?.getFeeLedgerRecordsToRefund || [];
    }

    const records = refundableRecords.getFeeLedgerRecordsToRefund;
    const grouped = records.reduce((acc: any, record: any) => {
      const invoiceId = record.invoice_id || 'no-invoice';
      if (!acc[invoiceId]) {
        acc[invoiceId] = {
          key: `group-${invoiceId}`,
          invoice_id: invoiceId,
          records: [],
          totalAmount: 0,
        };
      }
      acc[invoiceId].records.push(record);
      acc[invoiceId].totalAmount += record.amount;
      return acc;
    }, {});

    return Object.values(grouped);
  }, [refundableRecords, isStripePaymentProcessor]);

  const selectedSum = useMemo(() => {
    if (!refundableRecords?.getFeeLedgerRecordsToRefund) return 0;

    return refundableRecords.getFeeLedgerRecordsToRefund
      .filter((record: any) => selectedRecords.includes(record.record_id))
      .reduce((sum: number, record: any) => sum + record.amount, 0);
  }, [selectedRecords, refundableRecords]);

  const handleGroupSelection = (invoiceId: string, checked: boolean) => {
    if (!isStripePaymentProcessor) return;

    const group = groupedData.find((g: any) => g.invoice_id === invoiceId);
    if (!group) return;

    const groupRecordIds = group.records.map((r: any) => r.record_id);

    if (checked) {
      setSelectedRecords((prev) =>
        Array.from(new Set([...prev, ...groupRecordIds])),
      );
    } else {
      setSelectedRecords((prev) =>
        prev.filter((id: string) => !groupRecordIds.includes(id)),
      );
    }
  };

  const isGroupSelected = (invoiceId: string) => {
    if (!isStripePaymentProcessor) return false;

    const group = groupedData.find((g: any) => g.invoice_id === invoiceId);
    if (!group) return false;

    const groupRecordIds = group.records.map((r: any) => r.record_id);
    return groupRecordIds.every((id: string) => selectedRecords.includes(id));
  };

  const isGroupIndeterminate = (invoiceId: string) => {
    if (!isStripePaymentProcessor) return false;

    const group = groupedData.find((g: any) => g.invoice_id === invoiceId);
    if (!group) return false;

    const groupRecordIds = group.records.map((r: any) => r.record_id);
    const selectedInGroup = groupRecordIds.filter((id: string) =>
      selectedRecords.includes(id),
    );
    return (
      selectedInGroup.length > 0 &&
      selectedInGroup.length < groupRecordIds.length
    );
  };

  const columns = [
    {
      title: 'Container',
      dataIndex: ['assignment', 'container', 'unique_name'],
      key: 'container',
      width: 150,
      render: (name: string, record: any) => (
        <Link
          to={
            routes.CONTAINERS + '/' + record.assignment?.container?.container_id
          }
        >
          {name}
        </Link>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 80,
      render: (amount: number) => numeral(amount).format(CURRENCY_FORMAT),
    },
    {
      title: 'Assessed On',
      dataIndex: 'assessed_on',
      key: 'assessed_on',
      width: 160,
      render: (date: string) => dayjs(date).format('LLL'),
    },
    {
      title: 'Processed On',
      dataIndex: 'processed_on',
      key: 'processed_on',
      width: 160,
      render: (date: string) =>
        date ? dayjs(date).format('LLL') : '- not set -',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 100,
      render: (note: string) => note || '- not set -',
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRecords,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRecords(selectedRowKeys as string[]);
    },
  };

  const handleSubmit = async () => {
    try {
      if (selectedRecords.length === 0) {
        api.error({ message: 'Please select at least one record to refund' });
        return;
      }

      setIsRefunding(true);
      const formValues = await form.validateFields();
      const note = formValues.note || '';

      const result = await refundAssignment({
        variables: {
          assignment_id: assignmentId,
          amount: selectedSum,
          record_ids: selectedRecords,
          note,
        },
      });

      if (result.data?.refundAssignment) {
        api.success({ message: 'Records refunded successfully' });
        handleClose();
        onSuccess?.();
      } else {
        api.error({ message: 'Failed to refund records' });
      }
    } catch (error) {
      api.error({ message: 'Failed to refund records' });
    } finally {
      setIsRefunding(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedRecords([]);
    setIsRefunding(false);
    onClose();
  };

  const renderGroupedTable = () => {
    return (
      <div>
        {groupedData.map((group: any) => (
          <div
            key={group.key}
            style={{
              marginBottom: 16,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Checkbox
                  checked={isGroupSelected(group.invoice_id)}
                  indeterminate={isGroupIndeterminate(group.invoice_id)}
                  onChange={(e) =>
                    handleGroupSelection(group.invoice_id, e.target.checked)
                  }
                />
                <span style={{ fontWeight: 500 }}>
                  Invoice:{' '}
                  {group.invoice_id === 'no-invoice'
                    ? 'No Invoice'
                    : group.invoice_id}
                </span>
                <span style={{ color: '#666' }}>
                  ({group.records.length} records,{' '}
                  {numeral(group.totalAmount).format(CURRENCY_FORMAT)})
                </span>
              </div>
              {group.invoice_id !== 'no-invoice' && (
                <a
                  href={STRIPE_INVOICE_URL + group.invoice_id}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12 }}
                >
                  View in Stripe
                </a>
              )}
            </div>
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={group.records}
              rowKey="record_id"
              pagination={false}
              size="small"
              style={{ margin: 0 }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Refund Charges"
        open={isVisible}
        width={1000}
        onOk={handleSubmit}
        onCancel={handleClose}
        confirmLoading={isRefunding}
        okText={
          selectedRecords.length > 0
            ? `REFUND ${numeral(selectedSum).format(CURRENCY_FORMAT)} IN FEES`
            : 'REFUND FEES'
        }
      >
        <Form form={form} layout="vertical">
          {isStripePaymentProcessor ? (
            renderGroupedTable()
          ) : (
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={refundableRecords?.getFeeLedgerRecordsToRefund || []}
              rowKey="record_id"
              pagination={false}
              scroll={{ y: 300 }}
            />
          )}
          <Form.Item
            name="note"
            label="Note"
            style={{ marginTop: 24 }}
            rules={[
              {
                max: 245,
                message: 'Note cannot exceed 245 characters',
              },
            ]}
          >
            <Input.TextArea
              placeholder="Enter a note for this refund (optional)"
              maxLength={245}
              showCount
              rows={1}
            />
          </Form.Item>
          {selectedRecords.length > 0 && (
            <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
              Total amount to refund:{' '}
              {numeral(selectedSum).format(CURRENCY_FORMAT)}
            </Typography.Text>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default RefundFeeLedgerModal;
